import { Transition } from "@headlessui/react";
import { ReactNode, useContext, useEffect } from "react";
import { useLocation } from "react-router";
import { twMerge } from "tailwind-merge";
import { MenuDisclosureContext } from "../../contexts/MenuDisclosureContext";
import { useMenuDrawerContext } from "./MenuDrawerContextProvider.shared";
import { MenuDrawerListElement } from "./MenuDrawerListElement";

const useMenuDisclosureContext = () => {
  const context = useContext(MenuDisclosureContext);
  if (context === undefined) {
    throw new Error(
      "useMenuDisclosureContext must be within MenuDisclosureContextProvider",
    );
  }

  return context;
};

const MenuDisclosureRoot = ({
  children,
  routes,
  index,
}: {
  children: ReactNode;
  routes?: string[];
  index: number;
}) => {
  const location = useLocation();

  const { subMenuIndex, setSubMenuIndex } = useMenuDrawerContext();

  useEffect(() => {
    if (routes && routes.some((route) => location.pathname.startsWith(route))) {
      setSubMenuIndex(index);
    }
  }, [routes, location, index, setSubMenuIndex]);

  return (
    <MenuDisclosureContext.Provider value={{ active: index === subMenuIndex }}>
      <div>{children}</div>
    </MenuDisclosureContext.Provider>
  );
};

const Panel = ({ children }: { children: ReactNode }) => {
  const { isRailDisplayType } = useMenuDrawerContext();
  const { active } = useMenuDisclosureContext();

  if (!active) return null;

  return (
    <div
      className={twMerge(
        "border-grey-100 mt-1 mb-2 -ml-1 border-l-[3px] border-solid pl-1",
        !isRailDisplayType && "ml-4",
      )}
    >
      {children}
    </div>
  );
};

const Button = ({
  onClick,
  children,
}: {
  onClick: () => void;
  children: ReactNode;
}) => {
  return (
    <MenuDrawerListElement onClick={onClick} className={twMerge("mb-0")}>
      {children}
    </MenuDrawerListElement>
  );
};

export {
  MenuDisclosureRoot as MenuDisclosure,
  Transition as MenuDisclosureTransition,
  Button as MenuDisclosureButton,
  Panel as MenuDisclosurePanel,
};
